import { useState, useEffect } from "react";
import React from "react";
import { getFirestore, getDocs, getDoc, doc, updateDoc, collection, setDoc } from "firebase/firestore";
import app from "./firebase";
import { useCookies } from "react-cookie";
import { Helmet } from "react-helmet";
import {
  User, Mail, Phone, MapPin, Save, LogOut, Menu, X, ChevronRight, BookOpen,
  LayoutDashboard, History, Wallet, Settings as SettingsIcon, ShoppingCart, PhoneCall, Leaf, CheckCircle2, AlertCircle, Eye, EyeOff, Lock, Award, Clock, Bell, Shield, Trash2
} from "lucide-react";

const db = getFirestore(app);

const Settings = () => {
  const [name, setName] = useState("Investor");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDOB] = useState("");
  const [nationality, setNationality] = useState("");
  const [currency, setCurrency] = useState("");
  const [password, setPassword] = useState("");
  const [old_password, setOldPassword] = useState("");
  const [new_password, setNewPassword] = useState("");
  const [signin_notification, setSigninNotification] = useState(false);
  const [price_change_notification, setPriceChangeNotification] = useState(false);
  const [transaction_notification, setTransactionNotification] = useState(false);
  const [factor2, set2Fa] = useState(false);
  const [show_mobile_menu, setMobileMenu] = useState(false);
  const [walletDisplay, setWalletDisplay] = useState("0.00");
  const [showOldPass, setShowOldPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [toast, setToast] = useState(null);
  const [isAgent, setIsAgent] = useState(false);
  const [agentStatus, setAgentStatus] = useState("none"); // none, pending, approved, rejected
  const [agentId, setAgentId] = useState("");
  const [agentRejectReason, setAgentRejectReason] = useState("");
  const [agentRevokeReason, setAgentRevokeReason] = useState("");
  const [isRequestingAgent, setIsRequestingAgent] = useState(false);
  const [cookies, , removeCookie] = useCookies(["name"]);

  const userId = cookies.username;
  if (userId == undefined) window.location.href = "/login";

  const showToast = (type, title, msg) => {
    setToast({ type, title, msg });
    setTimeout(() => setToast(null), 5000);
  };

  useEffect(() => {
    if (show_mobile_menu) document.body.classList.add("lock-scroll");
    else document.body.classList.remove("lock-scroll");
    return () => document.body.classList.remove("lock-scroll");
  }, [show_mobile_menu]);

  useEffect(() => {
    const initiateData = async () => {
      try {
        const docRef = doc(db, "users", userId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const ud = { id: docSnap.id, ...docSnap.data() };
          setName(ud.name); setPhone(ud.phone); setEmail(ud.email);
          setDOB(ud.DOB); setNationality(ud.Nationality); setCurrency(ud.currency);
          set2Fa(ud.factor2); setPassword(ud.password);
          setWalletDisplay((ud.wallet).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,"));
          setIsAgent(ud.isAgent || false);
          setAgentStatus(ud.agentStatus || "none");
          setAgentId(ud.agentId || "");
          setAgentRejectReason(ud.agentRejectReason || "");
          setAgentRevokeReason(ud.agentRevokeReason || "");
        }
        const notifRef = doc(db, "notification", userId);
        const notifSnap = await getDoc(notifRef);
        if (notifSnap.exists()) {
          const nd = notifSnap.data();
          setSigninNotification(nd.signin);
          setPriceChangeNotification(nd.price_change);
          setTransactionNotification(nd.transaction);
        }
      } catch (error) { console.error(error); }
    };
    initiateData();
  }, []);

  const toggleNotif = async (field, current, setter, dbField) => {
    const newVal = !current;
    setter(newVal);
    await updateDoc(doc(db, "notification", userId), { [dbField]: newVal });
  };

  const toggle2FA = async () => {
    const newVal = !factor2;
    set2Fa(newVal);
    await updateDoc(doc(db, "users", userId), { factor2: newVal });
    showToast("success", "2FA Updated", `Two-factor authentication ${newVal ? "enabled" : "disabled"}.`);
  };

  const updatePassword = async () => {
    if (old_password.length < 4 || new_password.length < 4) { showToast("error", "Invalid Input", "Both fields need at least 4 characters."); return; }
    if (password !== old_password) { showToast("error", "Wrong Password", "Old password does not match."); return; }
    await updateDoc(doc(db, "users", userId), { password: new_password });
    setPassword(new_password); setOldPassword(""); setNewPassword("");
    showToast("success", "Password Updated", "Your password has been changed successfully.");
  };

  const deactivateAccount = async () => {
    if (!window.confirm("Are you sure you want to deactivate your account? This action cannot be undone.")) return;
    await updateDoc(doc(db, "users", userId), { status: "deactivate" });
    removeCookie("username"); window.location.href = "/login";
  };

  const handleRequestAgent = async () => {
    setIsRequestingAgent(true);
    try {
      const requestData = {
        userId,
        userName: name,
        status: "pending",
        timestamp: new Date().toISOString()
      };
      await setDoc(doc(db, "agent_requests", userId), requestData);
      await updateDoc(doc(db, "users", userId), { agentStatus: "pending" });
      setAgentStatus("pending");
      showToast("success", "Request Submitted", "Your application to become an agent is now pending approval.");
    } catch (error) {
      console.error(error);
      showToast("error", "Request Failed", "Could not submit agent request.");
    } finally {
      setIsRequestingAgent(false);
    }
  };

  const navItems = [
    { icon: <LayoutDashboard size={18} />, label: "Dashboard", action: () => { window.location.href = "/dashboard"; } },
    { icon: <History size={18} />, label: "Investment History", action: () => { window.location.href = "/history"; } },
    { icon: <Wallet size={18} />, label: "Wallet", action: () => { window.location.href = "/wallet"; } },
    { icon: <SettingsIcon size={18} />, label: "Settings", action: null, active: true },
    { icon: <ShoppingCart size={18} />, label: "Marketplace", action: () => { window.location.href = "/market_dashboard"; } },
    { icon: <BookOpen size={18} />, label: "Health & News (Blog)", action: () => { window.location.href = "/blog"; } },
    { icon: <PhoneCall size={18} />, label: "Contact Support", action: () => { window.location.href = "/contact"; } },
  ];

  const ToggleRow = ({ label, value, onToggle }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", borderBottom: "1px solid var(--color-gray-100)" }}>
      <span style={{ fontSize: "14px", color: "var(--color-gray-700)" }}>{label}</span>
      <button onClick={onToggle}
        style={{ width: "44px", height: "24px", borderRadius: "12px", background: value ? "var(--color-primary)" : "var(--color-gray-200)", border: "none", cursor: "pointer", position: "relative", transition: "background 0.2s" }}>
        <span style={{ position: "absolute", top: "2px", left: value ? "22px" : "2px", width: "20px", height: "20px", borderRadius: "50%", background: "white", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.15)" }} />
      </button>
    </div>
  );

  return (
    <div style={{ fontFamily: "var(--font-sans)", display: "flex", minHeight: "100vh", background: "var(--color-gray-50)" }}>
      <Helmet><title>Settings — Agrivestia</title></Helmet>

      {/* Toast */}
      {toast && (
        <div className="toast" style={{ borderLeft: `4px solid ${toast.type === "success" ? "var(--color-success)" : "var(--color-danger)"}` }}>
          <div className={`toast-icon ${toast.type === "success" ? "toast-success" : "toast-error"}`}>
            {toast.type === "success" ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          </div>
          <div className="toast-body"><div className="toast-title">{toast.title}</div><div className="toast-msg">{toast.msg}</div></div>
          <button className="toast-close" onClick={() => setToast(null)}>×</button>
        </div>
      )}

      {show_mobile_menu && <div className="overlay" onClick={() => setMobileMenu(false)} />}

      {/* Sidebar */}
      <aside className={`sidebar ${show_mobile_menu ? "open" : ""}`} style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
        <div style={{ padding: "24px 20px 16px", borderBottom: "1px solid var(--color-gray-100)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px" }}>
            <div style={{ width: "34px", height: "34px", borderRadius: "10px", background: "var(--gradient-accent)", display: "flex", alignItems: "center", justifyContent: "center" }}><Leaf size={17} color="white" /></div>
            <span style={{ fontWeight: 800, fontSize: "17px", color: "var(--color-primary-dark)", letterSpacing: "-0.02em" }}>AGRIVESTIA</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "var(--gradient-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "15px", color: "white" }}>{name ? name[0].toUpperCase() : "U"}</div>
            <div><div style={{ fontWeight: 600, fontSize: "13px", color: "var(--color-gray-800)" }}>{name}</div><div style={{ fontSize: "11px", color: "var(--color-gray-400)" }}>Agrivestia Farmer</div></div>
          </div>
        </div>
        <div style={{ margin: "16px", padding: "14px 16px", background: "var(--color-primary-light)", borderRadius: "var(--radius-md)", border: "1px solid var(--color-accent-light)" }}>
          <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--color-primary)", letterSpacing: "0.05em", marginBottom: "4px" }}>WALLET BALANCE</div>
          <div style={{ fontSize: "20px", fontWeight: 800, color: "var(--color-primary-dark)" }}>₦{walletDisplay}</div>
        </div>
        <nav style={{ padding: "0 12px", flex: 1, overflowY: "auto" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-gray-400)", letterSpacing: "0.07em", textTransform: "uppercase", padding: "4px 8px 10px" }}>Menu</div>
          {navItems.map((item, i) => (
            <button key={i} onClick={item.action ? () => { setMobileMenu(false); item.action(); } : undefined}
              className={`sidebar-link ${item.active ? "active" : ""}`} style={{ width: "100%", border: "none", cursor: "pointer", marginBottom: "2px" }}>
              <span>{item.icon}</span><span style={{ fontSize: "14px" }}>{item.label}</span>
              {item.active && <ChevronRight size={14} style={{ marginLeft: "auto" }} />}
            </button>
          ))}
        </nav>
        <div style={{ padding: "12px 12px 20px", borderTop: "1px solid var(--color-gray-100)" }}>
          <button className="sidebar-link danger" onClick={() => { removeCookie("username"); window.location.href = "/login"; }} style={{ width: "100%", border: "none", cursor: "pointer" }}>
            <LogOut size={17} /><span style={{ fontSize: "14px" }}>Log Out</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="main-content">
        <div style={{ background: "var(--color-white)", borderBottom: "1px solid var(--color-gray-100)", padding: "0 28px", height: "64px", display: "flex", alignItems: "center", gap: "16px", position: "sticky", top: 0, zIndex: 100 }}>
          <button onClick={() => setMobileMenu(!show_mobile_menu)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-gray-600)", display: "none" }} className="show-mobile">
            {show_mobile_menu ? <X size={22} /> : <Menu size={22} />}
          </button>
          <div>
            <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--color-gray-900)" }}>Account Settings</div>
            <div style={{ fontSize: "12px", color: "var(--color-gray-400)" }}>Manage your profile, security, and notifications</div>
          </div>
        </div>

        <div style={{ padding: "28px", maxWidth: "720px", display: "flex", flexDirection: "column", gap: "24px" }}>
          {/* Personal Details */}
          <div className="card" style={{ padding: "24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
              <div style={{ width: "34px", height: "34px", borderRadius: "10px", background: "var(--color-primary-light)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <User size={17} color="var(--color-primary)" />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: "15px", color: "var(--color-gray-800)" }}>Personal Details</div>
                <div style={{ fontSize: "12px", color: "var(--color-gray-400)", marginTop: "1px" }}>Contact us to update your personal information</div>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              {[["Full Name", name], ["Email", email], ["Phone", phone], ["Date of Birth", dob], ["Nationality", nationality], ["Currency", currency]].map(([label, val]) => (
                <div key={label} style={{ padding: "12px 14px", background: "var(--color-gray-50)", borderRadius: "var(--radius-md)", border: "1px solid var(--color-gray-100)" }}>
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--color-gray-400)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px" }}>{label}</div>
                  <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-gray-800)" }}>{val || "—"}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Notifications */}
          <div className="card" style={{ padding: "24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
              <div style={{ width: "34px", height: "34px", borderRadius: "10px", background: "var(--color-primary-light)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Bell size={17} color="var(--color-primary)" />
              </div>
              <div style={{ fontWeight: 700, fontSize: "15px", color: "var(--color-gray-800)" }}>Notification Settings</div>
            </div>
            <ToggleRow label="Sign-in notifications" value={signin_notification} onToggle={() => toggleNotif("signin", signin_notification, setSigninNotification, "signin")} />
            <ToggleRow label="Price change alerts" value={price_change_notification} onToggle={() => toggleNotif("price_change", price_change_notification, setPriceChangeNotification, "price_change")} />
            <ToggleRow label="Buy & sell confirmations" value={transaction_notification} onToggle={() => toggleNotif("transaction", transaction_notification, setTransactionNotification, "transaction")} />
          </div>

          {/* Security */}
          <div className="card" style={{ padding: "24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
              <div style={{ width: "34px", height: "34px", borderRadius: "10px", background: "var(--color-primary-light)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Shield size={17} color="var(--color-primary)" />
              </div>
              <div style={{ fontWeight: 700, fontSize: "15px", color: "var(--color-gray-800)" }}>Security</div>
            </div>

            <ToggleRow label="Two-factor authentication (2FA)" value={factor2} onToggle={toggle2FA} />

            <div style={{ marginTop: "20px" }}>
              <div style={{ fontWeight: 600, fontSize: "13px", color: "var(--color-gray-700)", marginBottom: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
                <Lock size={14} /> Change Password
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
                <div className="input-group" style={{ marginBottom: 0 }}>
                  <label className="input-label">Old Password</label>
                  <div style={{ position: "relative" }}>
                    <input id="old_password" type={showOldPass ? "text" : "password"} className="input-field" value={old_password} onChange={e => setOldPassword(e.target.value)} placeholder="Current password" style={{ paddingRight: "40px" }} />
                    <button onClick={() => setShowOldPass(!showOldPass)} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--color-gray-400)" }}>
                      {showOldPass ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
                <div className="input-group" style={{ marginBottom: 0 }}>
                  <label className="input-label">New Password</label>
                  <div style={{ position: "relative" }}>
                    <input id="new_password" type={showNewPass ? "text" : "password"} className="input-field" value={new_password} onChange={e => setNewPassword(e.target.value)} placeholder="New password" style={{ paddingRight: "40px" }} />
                    <button onClick={() => setShowNewPass(!showNewPass)} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--color-gray-400)" }}>
                      {showNewPass ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
              </div>
              <button className="btn-primary" onClick={updatePassword} style={{ padding: "10px 22px", fontSize: "13px" }}>Update Password</button>
            </div>
          </div>

          {/* Become an Agent */}
          <div className="card" style={{ padding: "24px", background: isAgent ? "var(--color-primary-light)" : "var(--color-white)", border: isAgent ? "1px solid var(--color-primary)" : "1px solid var(--color-gray-100)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ width: "34px", height: "34px", borderRadius: "10px", background: isAgent ? "var(--color-primary)" : "var(--color-primary-light)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Award size={17} color={isAgent ? "white" : "var(--color-primary)"} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "15px", color: "var(--color-gray-800)" }}>{isAgent && agentStatus === "approved" ? "Agrivestia Verified Agent" : "Become an Agent"}</div>
                  <div style={{ fontSize: "12px", color: "var(--color-gray-400)" }}>{isAgent && agentStatus === "approved" ? `Official Agent ID: ${agentId}` : "Partner with us and register your own clients"}</div>
                </div>
              </div>
              {isAgent && agentStatus === "approved" && (
                <div style={{ padding: "4px 12px", background: "var(--color-primary)", color: "white", borderRadius: "20px", fontSize: "11px", fontWeight: 700 }}>VERIFIED</div>
              )}
            </div>

            {agentStatus === "none" && (
              <div>
                <p style={{ fontSize: "13px", color: "var(--color-gray-600)", lineHeight: 1.6, marginBottom: "16px" }}>
                  Apply to become an Agrivestia Agent and unlock the ability to register and manage multiple clients. You'll be able to trade on their behalf and earn commissions.
                </p>
                <button className="btn-primary" onClick={handleRequestAgent} disabled={isRequestingAgent} style={{ padding: "10px 22px", fontSize: "13px" }}>
                  {isRequestingAgent ? "Submitting..." : "Submit Application"}
                </button>
              </div>
            )}

            {agentStatus === "pending" && (
              <div style={{ padding: "14px", background: "#fef9c3", borderRadius: "var(--radius-md)", border: "1px solid #fde047", display: "flex", alignItems: "center", gap: "10px" }}>
                <Clock size={16} color="#854d0e" />
                <div style={{ fontSize: "13px", color: "#854d0e", fontWeight: 500 }}>Your application is currently being reviewed by the admin.</div>
              </div>
            )}

            {agentStatus === "rejected" && (
              <div>
                <div style={{ padding: "14px", background: "var(--color-danger-light)", borderRadius: "var(--radius-md)", border: "1px solid #fca5a5", display: "flex", alignItems: "start", gap: "10px", marginBottom: "12px" }}>
                  <AlertCircle size={16} color="#991b1b" style={{ marginTop: "2px" }} />
                  <div>
                    <div style={{ fontSize: "13px", color: "#991b1b", fontWeight: 700 }}>Application Rejected</div>
                    <div style={{ fontSize: "12px", color: "#b91c1c", marginTop: "4px" }}>Reason: {agentRejectReason || "Information provided was insufficient."}</div>
                  </div>
                </div>
                <button className="btn-primary" onClick={handleRequestAgent} disabled={isRequestingAgent} style={{ padding: "10px 22px", fontSize: "13px" }}>
                  {isRequestingAgent ? "Submitting..." : "Re-apply Now"}
                </button>
              </div>
            )}

            {(agentStatus === "revoked" || agentStatus === "disabled") && (
              <div>
                <div style={{ padding: "14px", background: "var(--color-danger-light)", borderRadius: "var(--radius-md)", border: "1px solid #fca5a5", display: "flex", alignItems: "start", gap: "10px", marginBottom: "12px" }}>
                  <AlertCircle size={16} color="#991b1b" style={{ marginTop: "2px" }} />
                  <div>
                    <div style={{ fontSize: "14px", color: "#991b1b", fontWeight: 700 }}>
                      {agentStatus === "revoked" ? "Agent License Revoked" : "Agent Account Disabled"}
                    </div>
                    <div style={{ fontSize: "12px", color: "#b91c1c", marginTop: "4px", lineHeight: 1.5 }}>
                      <strong>Reason:</strong> {agentRevokeReason || (agentStatus === "revoked" ? "Your agent license has been suspended by the administrator." : "Your agent capabilities have been temporarily disabled.")}
                    </div>
                    <div style={{ fontSize: "12px", color: "#7f1d1d", marginTop: "8px", fontWeight: 600 }}>
                      Please contact Agrivestia Admin to resolve this and restore your agent capabilities.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isAgent && agentStatus === "approved" && (
              <div style={{ padding: "14px", background: "rgba(34,197,94,0.1)", borderRadius: "var(--radius-md)", border: "1px solid var(--color-primary)" }}>
                <div style={{ fontSize: "13px", color: "var(--color-primary-dark)", lineHeight: 1.6 }}>
                  Congratulations! You are an active agent. You can now register new clients from the <strong>Marketplace Dashboard</strong> under your managed list.
                </div>
              </div>
            )}
          </div>

          {/* Danger Zone */}
          <div className="card" style={{ padding: "24px", border: "1px solid #fca5a5" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
              <div style={{ width: "34px", height: "34px", borderRadius: "10px", background: "var(--color-danger-light)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Trash2 size={17} color="var(--color-danger)" />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: "15px", color: "var(--color-gray-800)" }}>Danger Zone</div>
                <div style={{ fontSize: "12px", color: "var(--color-gray-400)" }}>Permanent actions — proceed with caution</div>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px", background: "var(--color-danger-light)", borderRadius: "var(--radius-md)", border: "1px solid #fca5a5" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: "14px", color: "#991b1b" }}>Deactivate Account</div>
                <div style={{ fontSize: "12px", color: "#b91c1c", marginTop: "2px" }}>This permanently removes all your data.</div>
              </div>
              <button className="btn-danger" onClick={deactivateAccount} style={{ padding: "9px 18px", fontSize: "13px" }}>Deactivate</button>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        @media (max-width: 600px) {
          div[style*="grid-template-columns: 1fr 1fr"] { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default Settings;
